import React from 'react';

import { Button as MuiButton, ButtonProps as MuiButtonProps } from '@mui/material';

import { LoaderSpinner } from 'components/common/LodaerSpinner/LoaderSpinner';

import styles from './Button.module.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary';

export interface ButtonProps extends Omit<MuiButtonProps, 'variant' | 'color'> {
    /**
     * The variant of the button (primary, secondary, tertiary)
     */
    variant?: ButtonVariant;
    /**
     * If true, the button will have destructive styling
     */
    destructive?: boolean;
    /**
     * If true, the button will be circular/round
     */
    circle?: boolean;
    /**
     * The content of the button
     */
    children: React.ReactNode;

    isLoading?: boolean;
}

/**
 * Custom Button component based on MUI Button
 * Implements design system styling from Figma atoms
 * Supports light and dark themes via CSS variables
 */
export const Button: React.FC<ButtonProps> = ({
    variant = 'primary',
    destructive = false,
    disabled = false,
    circle = false,
    children,
    className = '',
    isLoading,
    ...props
}) => {
    const buttonClass = [
        styles.button,
        styles[variant],
        destructive ? styles.destructive : '',
        circle ? styles.circle : '',
        className,
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <MuiButton className={buttonClass} disabled={disabled} disableElevation {...props}>
            {children}{' '}
            {isLoading ? (
                <span className={styles.spinnerWrapper}>
                    <LoaderSpinner
                        color={variant === 'primary' ? 'var(--button-primary-fg)' : 'var(--fg-brand-primary)'}
                    />
                </span>
            ) : null}
        </MuiButton>
    );
};

export default Button;
